import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface MetricCardProps {
  value: number | string;
  label: string;
  icon?: LucideIcon;
  unit?: string;
  prefix?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'stable';
    label?: string;
  };
  insights?: string[];
  size?: 'sm' | 'md' | 'lg';
  color?: 'default' | 'green' | 'red' | 'blue' | 'amber';
}

const colorClasses = {
  default: 'bg-secondary/50',
  green: 'bg-emerald-100 dark:bg-emerald-900/30',
  red: 'bg-rose-100 dark:bg-rose-900/30',
  blue: 'bg-blue-100 dark:bg-blue-900/30',
  amber: 'bg-amber-100 dark:bg-amber-900/30',
};

const iconColorClasses = {
  default: 'text-primary',
  green: 'text-emerald-600 dark:text-emerald-400',
  red: 'text-rose-600 dark:text-rose-400',
  blue: 'text-blue-600 dark:text-blue-400',
  amber: 'text-amber-600 dark:text-amber-400',
};

export default function MetricCard({
  value,
  label,
  icon: Icon,
  unit,
  prefix,
  trend,
  insights,
  size = 'md',
  color = 'default',
}: MetricCardProps) {
  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString('th-TH', { maximumFractionDigits: 2 });
  };

  const sizeClasses = {
    sm: { container: 'p-3', value: 'text-xl', label: 'text-xs', icon: 'h-4 w-4' },
    md: { container: 'p-4', value: 'text-2xl', label: 'text-sm', icon: 'h-5 w-5' },
    lg: { container: 'p-5', value: 'text-3xl', label: 'text-base', icon: 'h-6 w-6' },
  };

  const classes = sizeClasses[size];

  return (
    <div className={`rounded-lg ${colorClasses[color]} ${classes.container}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`${classes.label} text-muted-foreground mb-1`}>{label}</p>
          <div className="flex items-baseline gap-1">
            {prefix && <span className={`${classes.value} font-bold`}>{prefix}</span>}
            <span className={`${classes.value} font-bold`}>{formatValue(value)}</span>
            {unit && <span className="text-muted-foreground ml-1">{unit}</span>}
          </div>

          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trend.direction === 'up' ? (
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              ) : trend.direction === 'down' ? (
                <TrendingDown className="h-4 w-4 text-rose-500" />
              ) : (
                <Minus className="h-4 w-4 text-muted-foreground" />
              )}
              <span
                className={`text-xs font-medium ${
                  trend.direction === 'up'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : trend.direction === 'down'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-muted-foreground'
                }`}
              >
                {trend.value > 0 ? '+' : ''}{trend.value}%
                {trend.label && <span className="text-muted-foreground ml-1">{trend.label}</span>}
              </span>
            </div>
          )}
        </div>

        {Icon && (
          <div className={`p-2 rounded-full ${colorClasses[color]}`}>
            <Icon className={`${classes.icon} ${iconColorClasses[color]}`} />
          </div>
        )}
      </div>

      {insights && insights.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
          {insights.map((insight, i) => (
            <p key={i} className="text-xs text-muted-foreground">
              {insight}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
